import React, { useEffect, useState } from 'react';

import constants from 'constants/constants';
import { Controller, useForm } from 'react-hook-form';

import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { TextInput } from 'components/DesignSystem/TextInput';
import { CustomizedFormErrorMessage } from 'components/common';

import classes from './RenameViewModal.module.scss';

interface Props {
    isModalOpen: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    initialName: string;
    onSubmit: (newName: string) => Promise<void>;
}

interface FormValues {
    newName: string;
}

const FORM_ID = 'rename-schedule-view-form';

export const RenameViewModal = ({ isModalOpen, setIsModalOpen, initialName, onSubmit }: Props) => {
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const form = useForm<FormValues>({
        defaultValues: {
            newName: initialName,
        },
    });

    useEffect(() => {
        if (isModalOpen) {
            form.reset({ newName: initialName });
        }
    }, [isModalOpen, initialName]);

    const hideModal = () => {
        setIsModalOpen(false);
    };

    const submitForm = async (formValues: FormValues) => {
        const newName = formValues.newName;

        setIsLoadingSubmit(true);
        try {
            await onSubmit(newName);
            hideModal();
        } finally {
            setIsLoadingSubmit(false);
        }
    };

    return (
        <div className={classes.renameViewModal}>
            <Modal
                open={isModalOpen}
                onClose={hideModal}
                title={'Rename view'}
                width={constants.DEFAULT_MODAL_WIDTH}
                actions={
                    <>
                        <Button variant={'secondary'} onClick={hideModal}>
                            Cancel
                        </Button>
                        <Button isLoading={isLoadingSubmit} type={'submit'} form={FORM_ID}>
                            Rename
                        </Button>
                    </>
                }
            >
                <form id={FORM_ID} onSubmit={form.handleSubmit(submitForm)}>
                    <div className={classes.inputWrapper}>
                        <label htmlFor={'newName'}>View name:</label>
                        <Controller
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    error={Boolean(form.formState.errors.newName)}
                                    onClear={() => form.setValue('newName', '')}
                                    placeholder={'Enter view name...'}
                                />
                            )}
                            name={'newName'}
                            control={form.control}
                            rules={{
                                required: 'View name is required',
                            }}
                        />
                        {form.formState.errors.newName && (
                            <CustomizedFormErrorMessage text={form.formState.errors.newName.message} />
                        )}
                    </div>
                </form>
            </Modal>
        </div>
    );
};
